import { compareAscending } from './sortBy';

type Iteratee<T> = ((item: T) => unknown) | keyof T;
type Order = 'asc' | 'desc';

const resolve = <T>(iteratee: Iteratee<T>, item: T): unknown =>
    typeof iteratee === 'function' ? iteratee(item) : item[iteratee];

const toArray = <V>(value: V | V[]): V[] => (Array.isArray(value) ? value : [value]);

/**
 * Native replacement for lodash/fp `orderBy`. Returns a new array sorted by one
 * or more iteratees (functions or property keys), each with an `'asc'`/`'desc'`
 * order (defaulting to `'asc'`). Stable and non-mutating.
 */
export default function orderBy<T>(
    collection: readonly T[] | null | undefined,
    iteratees: Iteratee<T> | Iteratee<T>[],
    orders: Order | Order[] = [],
): T[] {
    const its = toArray(iteratees);
    const ords = toArray(orders);

    return [...(collection ?? [])].sort((a, b) => {
        for (let i = 0; i < its.length; i++) {
            const comparison = compareAscending(resolve(its[i], a), resolve(its[i], b));
            if (comparison !== 0) {
                return ords[i] === 'desc' ? -comparison : comparison;
            }
        }
        return 0;
    });
}
